// Окно «Войдите снова» (T045; contracts/ui.md, «Сеанс и связь»; spec, сценарий 5.4): сеанс
// закончился (AUTH-004) — окно поверх того же экрана, почта уже введена. Запросы, получившие
// AUTH-004, ждут входа: после входа тем же пользователем они повторяются, и действие
// завершается, будто сеанс не заканчивался, — введённое не теряется. Вошёл другой
// пользователь — данные прежнего убираются, переход на главную (FR-008в).
// Отмена — запросы завершаются ошибкой AUTH-004 с тостом, как обычный отказ. Отказ сервера
// по полям — текст под полем, код и номер операции — в выноске над полями (T058); фокус —
// в первое поле с ошибкой (ux.md, раздел 13; T162).
import { fieldMessages } from '@kp/contracts';
import type { MeResponse } from '@kp/contracts';
import { Button, Callout, Modal, ModalFooter, TextField } from '@kp/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { api, isApiError, setReauthHandler } from '../api/client.ts';
import type { ReauthRequest } from '../api/client.ts';
import { FieldsRefusalCallout } from '../app/FieldsRefusal.tsx';
import { dropAllDeferred } from '../app/deferred.ts';
import { errorView, hasFieldTexts, loginFailureView } from '../app/errors.ts';
import type { ErrorView, FieldTexts, FieldsRefusal } from '../app/errors.ts';
import { meQueryKey } from './session.ts';

interface Waiting {
  request: ReauthRequest;
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}

type Fields = 'email' | 'password';

export function ReauthModal() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const formId = useId();
  const queue = useRef<Waiting[]>([]);
  const openRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Partial<Record<Fields, string>>>({});
  const [failure, setFailure] = useState<ErrorView | null>(null);
  const [refusal, setRefusal] = useState<FieldsRefusal<Fields> | null>(null);
  const [pending, setPending] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(
    () =>
      setReauthHandler((request) => {
        // Сеанса в памяти нет — приложение только открывается: вход решает защита маршрута.
        const me = queryClient.getQueryData<MeResponse>(meQueryKey);
        if (!me) return null;
        return new Promise((resolve, reject) => {
          queue.current.push({ request, resolve, reject });
          if (openRef.current) return;
          openRef.current = true;
          setEmail(me.user.email);
          setPassword('');
          setErrors({});
          setFailure(null);
          setRefusal(null);
          setOpen(true);
        });
      }),
    [queryClient],
  );

  const close = () => {
    openRef.current = false;
    setOpen(false);
    setPassword('');
  };

  const cancel = () => {
    const waiting = queue.current;
    queue.current = [];
    close();
    for (const item of waiting) item.reject(item.request.error);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (pending) return;
    setRefusal(null);
    const found: Partial<Record<Fields, string>> = {};
    if (email.trim() === '') found.email = fieldMessages.loginEmail;
    if (password === '') found.password = fieldMessages.password;
    setErrors(found);
    if (found.email || found.password) {
      (found.email ? emailRef : passwordRef).current?.focus();
      return;
    }
    setPending(true);
    setFailure(null);
    let fresh: MeResponse;
    try {
      await api('auth.login', { body: { email: email.trim(), password } });
      fresh = await api('auth.me');
    } catch (error) {
      setPending(false);
      const fields: FieldTexts<Fields> = isApiError(error, 'REC-007')
        ? { email: error.fields.email, password: error.fields.password }
        : {};
      // Отказ по полям — под полями и выноска с кодом; не попал ни в одно поле — целиком.
      if (hasFieldTexts(fields)) {
        setErrors(fields);
        setRefusal({ fields, view: errorView(error) });
        (fields.email ? emailRef : passwordRef).current?.focus();
        return;
      }
      setFailure(loginFailureView(error));
      passwordRef.current?.focus();
      return;
    }
    setPending(false);
    const previous = queryClient.getQueryData<MeResponse>(meQueryKey);
    const waiting = queue.current;
    queue.current = [];
    close();
    queryClient.setQueryData(meQueryKey, fresh);
    if (previous?.user.id === fresh.user.id) {
      for (const item of waiting) item.request.retry().then(item.resolve, item.reject);
      return;
    }
    // Другой пользователь: данные прежнего не показываются, его действия не повторяются —
    // ожидающие запросы остаются без ответа и уходят вместе с экраном, отложенные изменения
    // (удаление выгрузки) отбрасываются: сеансом другого пользователя они не уходят (T121).
    dropAllDeferred();
    queryClient.removeQueries({ predicate: (query) => query.queryKey[1] !== 'auth.me' });
    await navigate({ to: '/' });
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next && !pending) cancel();
      }}
      size="sm"
      title="Войдите снова"
      description="Сеанс закончился. Введённое не потеряется: после входа действие повторится"
      footer={
        <ModalFooter
          primary={
            <Button type="submit" form={formId} variant="primary" loading={pending}>
              Войти
            </Button>
          }
        />
      }
    >
      <form id={formId} className="t-col" noValidate onSubmit={(event) => void submit(event)}>
        {failure ? (
          <Callout
            tone="bad"
            role="alert"
            lead={`${failure.message}.`}
            code={failure.code}
            operation={failure.operation}
          >
            {failure.nextStep}
          </Callout>
        ) : null}
        <FieldsRefusalCallout refusal={refusal} errors={errors} />
        <TextField
          ref={emailRef}
          label="Почта"
          type="email"
          inputMode="email"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          required
          value={email}
          error={errors.email}
          onChange={(event) => {
            setEmail(event.target.value);
            if (errors.email) setErrors((current) => ({ ...current, email: undefined }));
          }}
        />
        <TextField
          ref={passwordRef}
          label="Пароль"
          type="password"
          autoComplete="current-password"
          required
          data-autofocus
          value={password}
          error={errors.password}
          onChange={(event) => {
            setPassword(event.target.value);
            if (errors.password) setErrors((current) => ({ ...current, password: undefined }));
          }}
        />
      </form>
    </Modal>
  );
}
